import React, { useContext, useState } from "react";

import { AuthContext } from "contexts/AuthContext";
import { CompetitionContext } from "contexts/CompetitionContext";

import { parseStageDate } from "helpers";
import { WorkflowStep } from "model/dictionary";

import {
  Circle,
  DetailFoot,
  Explain,
  ExpandButton,
  Head,
  HereBadge,
  StatusBadge,
  Step,
  StepDate,
  StepLink,
  StepName,
  StepRole,
  Steps,
  Title,
  WorkflowContainer,
} from "./styles";

// Los iconos son de la misma familia que las acciones de `ListOfEntries`
// (grilla de 24, trazo de 2, `currentColor`); sólo los usa este componente.
const Icon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const icons = {
  entry: (
    <Icon>
      <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </Icon>
  ),
  review: (
    <Icon>
      <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2-5.5-2.9-5.5 2.9 1-6.2L3 9.6l6.2-.9z" />
    </Icon>
  ),
  finalists: (
    <Icon>
      <path d="M4 7l2 2 3-3M4 15l2 2 3-3M13 8h7M13 16h7" />
    </Icon>
  ),
  voting: (
    <Icon>
      <path d="M9 12l2 2 4-4" />
      <path d="M5 7a2 2 0 012-2h10a2 2 0 012 2v12H5V7z" />
      <path d="M22 19H2" />
    </Icon>
  ),
  winners: (
    <Icon>
      <path d="M8 4h8v5a4 4 0 01-8 0z" />
      <path d="M8 6H5a3 3 0 003 4M16 6h3a3 3 0 01-3 4M12 13v4M8 21h8M10 17h4v4h-4z" />
    </Icon>
  ),
};

const ChevronIcon = () => (
  <Icon>
    <path d="M6 9l6 6 6-6" />
  </Icon>
);

// Los pasos del ciclo de una postulación, en orden. `status` es el estado en
// el que el paso deja la postulación (los rótulos de `model/options`); los
// pasos que no cambian el estado no lo llevan. `canVisit` decide si el paso es
// un link según los roles de quien mira; Postulación no lo tiene porque es
// sólo descriptiva: no es una pantalla del jurado.
const steps = [
  {
    key: WorkflowStep.ENTRY,
    name: "Postulación",
    role: "Postulantes",
    status: "Confirmado",
    explain:
      "Se completa el formulario y se confirma desde el mail (o a mano, desde el listado de postulaciones).",
  },
  {
    key: WorkflowStep.REVIEW,
    name: "Preselección",
    role: "Seleccionadores",
    href: "/jurado/preseleccion",
    canVisit: (roles) => roles.selector,
    explain:
      "Cada seleccionador valora las postulaciones confirmadas según los criterios.",
  },
  {
    key: WorkflowStep.FINALISTS,
    name: "Finalistas",
    role: "Administración",
    status: "Finalista",
    href: "/jurado/finalistas",
    canVisit: (roles) => roles.administrator,
    explain: "Se eligen por puntaje las que pasan a la votación.",
  },
  {
    key: WorkflowStep.VOTING,
    name: "Votación",
    role: "Jurado",
    href: "/jurado/votacion",
    canVisit: (roles) => roles.judge,
    explain: "Cada jurado vota una finalista por premio.",
  },
  {
    key: WorkflowStep.WINNERS,
    name: "Ganadores",
    role: "Administración",
    status: "Ganador",
    href: "/jurado/ganadores",
    canVisit: (roles) => roles.administrator,
    explain: "Se elige por votos el ganador de cada premio.",
  },
];

const readable = (date, options) =>
  new Intl.DateTimeFormat("es-AR", options).format(date);

const dayMonth = (date) => readable(date, { day: "numeric", month: "long" });

// "Del 7 al 12 de octubre" si caen en el mismo mes, "Del 26 de septiembre al
// 6 de octubre" si no. Con una sola punta, "Desde el" o "Hasta el".
const dateRange = (from, to) => {
  if (from && to) {
    const start =
      from.getMonth() === to.getMonth() ? from.getDate() : dayMonth(from);

    return `Del ${start} al ${dayMonth(to)}`;
  }

  if (from) {
    return `Desde el ${dayMonth(from)}`;
  }

  return to ? `Hasta el ${dayMonth(to)}` : "";
};

const dayAfter = (date) =>
  date &&
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

// Las fechas de cada paso salen de la competición vigente. Las públicas
// (`entryStage`, `selectionStage`, `judgingStage`) están siempre; las internas
// (`curationStage`, `finalistsStage`, `votingStartStage`, `votingEndStage`) son
// opcionales y sólo se muestran acá, que lo ven quienes participan del
// proceso. La preselección arranca el día siguiente al cierre de
// postulaciones: la ventana de postulación incluye `selectionStage` (ver
// `CompetitionContext`).
const stepDates = (competition) => {
  if (!competition) {
    return {};
  }

  const stage = (field) => parseStageDate(competition[field]);

  const finalists = stage("finalistsStage");
  const awards = stage("judgingStage");

  return {
    [WorkflowStep.ENTRY]: dateRange(stage("entryStage"), stage("selectionStage")),
    [WorkflowStep.REVIEW]: dateRange(
      dayAfter(stage("selectionStage")),
      stage("curationStage")
    ),
    [WorkflowStep.FINALISTS]: finalists
      ? `Se comunican el ${dayMonth(finalists)}`
      : "",
    [WorkflowStep.VOTING]: dateRange(
      stage("votingStartStage"),
      stage("votingEndStage")
    ),
    [WorkflowStep.WINNERS]: awards
      ? `Entrega de premios el ${dayMonth(awards)}`
      : "",
  };
};

// Si el detalle queda abierto o cerrado es una preferencia de cada navegador.
// El storage puede no estar (ventana privada, bloqueado): se cae a cerrado.
const STORAGE_KEY = "entryWorkflowExpanded";

const readExpanded = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch (error) {
    return false;
  }
};

const saveExpanded = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, `${value}`);
  } catch (error) {
    // Sin storage el detalle vuelve a cerrarse en la próxima pantalla.
  }
};

/**
 * Gráfico del ciclo de una postulación. En las pantallas del jurado marca en
 * qué paso está la pantalla (`current`, "Estás acá"); sin `current` —la home—
 * marca los pasos que le tocan a quien mira según sus roles ("Tu parte").
 * Desplegado, explica qué pasa en cada paso y con qué fechas de la edición.
 */
export default function EntryWorkflow({ current, overHero = true }) {
  const { currentUserRoles } = useContext(AuthContext);
  const { currentCompetition } = useContext(CompetitionContext);

  const [expanded, setExpanded] = useState(readExpanded);

  const dates = stepDates(currentCompetition);

  const onToggle = () => {
    setExpanded((prev) => {
      saveExpanded(!prev);

      return !prev;
    });
  };

  return (
    <WorkflowContainer
      aria-label="Proceso de las postulaciones"
      $overHero={overHero}
    >
      <Head>
        <Title>Proceso de las postulaciones</Title>

        <ExpandButton
          type="button"
          aria-expanded={expanded}
          aria-controls="entry-workflow-steps"
          onClick={onToggle}
        >
          {expanded ? "Ocultar detalle" : "Ver detalle"}
          <ChevronIcon />
        </ExpandButton>
      </Head>

      <Steps id="entry-workflow-steps">
        {steps.map((step) => {
          const isCurrent = step.key === current;
          const hasRole = Boolean(
            step.canVisit && step.canVisit(currentUserRoles)
          );
          const visitable = !isCurrent && hasRole;
          const highlighted = current ? isCurrent : hasRole;

          return (
            <Step
              key={step.key}
              data-highlight={highlighted || undefined}
              aria-current={isCurrent ? "step" : undefined}
            >
              <Circle>{icons[step.key]}</Circle>

              {visitable ? (
                <StepLink href={step.href}>{step.name}</StepLink>
              ) : (
                <StepName>{step.name}</StepName>
              )}

              {highlighted && (
                <HereBadge>{current ? "Estás acá" : "Tu parte"}</HereBadge>
              )}

              <StepRole>{step.role}</StepRole>

              {step.status && <StatusBadge>{step.status}</StatusBadge>}

              {expanded && dates[step.key] && (
                <StepDate>{dates[step.key]}</StepDate>
              )}

              {expanded && <Explain>{step.explain}</Explain>}
            </Step>
          );
        })}
      </Steps>

      {expanded && (
        <DetailFoot>
          Las fechas son las de la edición en curso y se cargan en
          Administración &gt; Competiciones.
        </DetailFoot>
      )}
    </WorkflowContainer>
  );
}
